import React, { useState } from "react";
import { Modal } from "react-bootstrap";
import { IonIcon } from "@ionic/react";
import {
  chevronForward,
  imagesOutline,
  locationOutline,
  openOutline,
  pricetagOutline,
  resizeOutline,
} from "ionicons/icons";
import ImageGallery from "react-image-gallery";
import "react-image-gallery/styles/css/image-gallery.css";
import ProgressiveImg from "./ProgressiveImg";

// Casas / Empleo tabs: listing cards that open the full listing in a modal.

// Splits listing text into paragraphs, short "Heading:" lines and "-" bullet lists.
function textBlocks(text) {
  const blocks = [];
  const lines = text.split(/\n+/).map((l) => l.trim()).filter(Boolean);
  for (const line of lines) {
    if (line.startsWith("-")) {
      const item = line.replace(/^-+\s*/, "");
      const last = blocks[blocks.length - 1];
      if (last && last.type === "list") last.items.push(item);
      else blocks.push({ type: "list", items: [item] });
    } else if (line.endsWith(":") && line.length <= 40) {
      blocks.push({ type: "heading", text: line });
    } else {
      blocks.push({ type: "paragraph", text: line });
    }
  }
  return blocks;
}

function ListingText({ text }) {
  return (
    <div className="listing-text">
      {textBlocks(text).map((block, i) => {
        if (block.type === "list") {
          return (
            <ul key={i}>
              {block.items.map((item, j) => (
                <li key={j}>{item}</li>
              ))}
            </ul>
          );
        }
        if (block.type === "heading") return <h4 key={i}>{block.text}</h4>;
        return <p key={i}>{block.text}</p>;
      })}
    </div>
  );
}

const excerpt = (text) => text.replace(/\s*\n+\s*/g, " ").trim();

function ListingCard({ className = "", onOpen, children }) {
  const onKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onOpen();
    }
  };
  return (
    <article
      className={`listing-card ${className}`}
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={onKeyDown}
    >
      {children}
    </article>
  );
}

function ListingModal({ show, onHide, title, url, children }) {
  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      scrollable
      size="lg"
      fullscreen="sm-down"
      dialogClassName="listing-modal"
    >
      <Modal.Header closeButton>
        <Modal.Title as="h3">{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>{children}</Modal.Body>
      <Modal.Footer>
        <a
          className="profile-cta listing-cta"
          href={url}
          target="_blank"
          rel="noopener noreferrer"
        >
          Saber más
          <IonIcon icon={openOutline} />
        </a>
      </Modal.Footer>
    </Modal>
  );
}

function HouseFacts({ house, withAddress }) {
  return (
    <div className="listing-facts">
      <span className="listing-price">
        <IonIcon icon={pricetagOutline} />
        {house.price} €
      </span>
      <span>
        <IonIcon icon={resizeOutline} />
        {house.size} m²
      </span>
      {withAddress && house.address && (
        <span>
          <IonIcon icon={locationOutline} />
          {house.address}
        </span>
      )}
    </div>
  );
}

function HouseItem({ house }) {
  const [open, setOpen] = useState(false);
  const photos = house.images.map((img) => ({
    original: img.url,
    thumbnail: img.url,
    originalAlt: house.title,
  }));

  return (
    <>
      <ListingCard onOpen={() => setOpen(true)}>
        <div className="listing-media">
          <ProgressiveImg src={house.images[0].url} alt={house.title} />
          <span className="listing-badge">
            <IonIcon icon={imagesOutline} />
            {house.images.length}
          </span>
        </div>
        <div className="listing-body">
          <h3 className="listing-title">{house.title}</h3>
          <HouseFacts house={house} />
          <p className="listing-excerpt">{excerpt(house.description)}</p>
        </div>
      </ListingCard>

      <ListingModal
        show={open}
        onHide={() => setOpen(false)}
        title={house.title}
        url={house.url}
      >
        <ImageGallery items={photos} showPlayButton={false} lazyLoad />
        <HouseFacts house={house} withAddress />
        <ListingText text={house.description} />
      </ListingModal>
    </>
  );
}

function JobItem({ job }) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <ListingCard className="listing-row" onOpen={() => setOpen(true)}>
        <div className="listing-logo">
          <img src={job.image} alt="" loading="lazy" />
        </div>
        <div className="listing-body">
          <h3 className="listing-title">{job.title}</h3>
          <p className="listing-excerpt">{excerpt(job.description)}</p>
        </div>
        <IonIcon icon={chevronForward} className="listing-chevron" />
      </ListingCard>

      <ListingModal
        show={open}
        onHide={() => setOpen(false)}
        title={job.title}
        url={job.url}
      >
        <div className="listing-logo listing-logo-lg">
          <img src={job.image} alt="" />
        </div>
        <ListingText text={job.description} />
      </ListingModal>
    </>
  );
}

export function HouseList({ houses }) {
  return (
    <div className="listing-list">
      {houses.map((house, index) => (
        <HouseItem key={index} house={house} />
      ))}
    </div>
  );
}

export function JobList({ jobs }) {
  return (
    <div className="listing-list">
      {jobs.map((job, index) => (
        <JobItem key={index} job={job} />
      ))}
    </div>
  );
}
